import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { CryptoService } from './crypto.service';

// Import de nm-socle-application
import { ConfigService } from './config.service';

export interface SecurityDefinition {
    target: string;
    method: string;
    base64: boolean;
    nb_iteration: number;
    nb_iteration_server: number;
}

export interface SecurityDefinitionMap {
    [key: string]: SecurityDefinition;
}

@Injectable()
export class SecurityService {
    public data: SecurityDefinitionMap = {};

    public lastCall : number|null = null;

    /**
     * @constructor
     * @description Service de stockage des paramètres de sécurité
     * @param {CryptoService} cryptoService service de crypto
     * @param {ConfigService} configService service de configuration
     * @param {Http} http service http angular
     */
    constructor(
        private cryptoService: CryptoService,
        private configService: ConfigService
        ) {
    };

    /**
     * @name setParameters
     * @description Initialise tous les paramètres d'un seul coup
     * @param {SecurityDefinition[]} parameters Tableau des paramètres de sécurité
     */
    public setParameters(parameters: SecurityDefinition[]) {
        for (let param of parameters) {
            this.data[param.target] = param;
        }
    }

    /**
     * @name getParameters
     * @description
     */
    public getParameters(): SecurityDefinitionMap {
        return this.data;
    }

    /**
     * @name put
     * @description Ajoute une clé,valeur
     * @param {string} key La clé
     * @param {SecurityDefinition} value La valeur
     */
    public put(key: string, value: SecurityDefinition) {
        if (this.data[key] === undefined) {
            this.data[key] = value;
        } else {
            throw `Valeur de sécurité ${key} déjà positionnée`;
        }
    }

    /**
     * @name get
     * @description Lit la valeur d'une clé
     * @param {string} key La clé
     */
    public get(key: string): SecurityDefinition {
        if (Object.keys(this.data).length === 0) {
            throw Error(`Aucune valeur de sécurité définie`);
        }

        if (this.data[key] === null || this.data[key] === undefined) {
            throw Error(`La valeur de ${key}  n' est pas renseignée dans les données de sécurité.`);
        }

        return this.data[key];
    }

    /**
     * @name getWSSEHeader
     * @description Retourne la valeurs des headers wsse sous forme de string
     * @param {string} username utilisateur
     * @param {string} pwd Password
     * @param {boolean} pwdEncrypted Si le mdp est déjà encrypté ou non
     * @return {string} headers
     */
    public getWSSEHeader(username: string, pwd: string): string {
        let wsseParams = this.generateWSSEParams(pwd);
        return `UsernameToken Username="${username}", PasswordDigest="${wsseParams.pwdDigest}", Nonce="${wsseParams.b64Nonce}", Created="${wsseParams.timestampString}"`;
    }

    /**
     * @name generateWSSEParams
     * @description Retourne les valleurs des headers WSSE
     * @param {string} encryptedPwd pwd hashé
     * @return {Object} valeurs des headers WSSE
     */
    private generateWSSEParams(encryptedPwd: string): any {
        let wsseEncodingConfig: SecurityDefinition = this.get('wsse');
        let strNonce = this.cryptoService.getSalt(8).toString();
        let b64_nonce = btoa(strNonce);
        let tt = new Date();
        // car sur le serveur mois 1-12 et en UTC JS 0-11
        let month = tt.getUTCMonth() + 1;

        let timestampString = tt.getFullYear() + "-"
            + ((month < 10) ? "0" : "") + month + "-"
            + ((tt.getUTCDate() < 10) ? "0" : "") + tt.getUTCDate() + "T"
            + ((tt.getUTCHours() < 10) ? "0" : "") + tt.getUTCHours() + ":"
            + ((tt.getUTCMinutes() < 10) ? "0" : "") + tt.getUTCMinutes() + ":"
            + ((tt.getUTCSeconds() < 10) ? "0" : "") + tt.getUTCSeconds() + "Z";

        let pwdDigest = this.cryptoService.encode(strNonce + timestampString + encryptedPwd, wsseEncodingConfig);

        return {
            'pwdDigest': pwdDigest,
            'b64Nonce': b64_nonce,
            'timestampString': timestampString
        };
    }

    /**
     * @name generateNmToken
     * @description Retourne un token de connexion à netagis maps  
     * @returns {string} encryptedConnectionString token encrypté en xor
     *
     * @memberOf SecurityService
     *
     */
    public generateNmToken() {
        let clefXOR = this.cryptoService.getKey();

        let connectionString = this.configService.get('customerId') + '|' + this.configService.get('login') + '|' + this.configService.get('pwd') + '|0|' + Date.now();
        let encryptedConnectionString = this.cryptoService.XOREncrypt(connectionString, clefXOR);
        return encryptedConnectionString;
    }

    public updateLastCall()
    {
        try { // pour les iframes externes
            if (window.top.document.URL == window.document.URL)
                this.lastCall = Date.now();
            else if (typeof(window.top['updateLastCall']) == 'function')
                window.top['updateLastCall']();
        }
        catch(ex) {}

        return this;
    }

    public checkLoginTimeOut() {
        if (this.lastCall + this.configService.get('loginTimeOut') < Date.now()) {
            return false;
        }
        return this.configService.get('loginTimeOut') - (Date.now() - this.lastCall);
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/security.service.ts